import React from 'react';
import { Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PlacementActivitySummaryRow } from './styles';

// dd/MM/yyyy — matches the format used in the Placement Activity tab.
function formatDate(isoString) {
  if (!isoString) {
    return '';
  }
  const d = new Date(isoString);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Compact one-line summary of the placement activity for a bench row (ADR-008).
 *
 * Four states:
 *   - activeFtProject == null                 → muted "no FT engagement" line.
 *   - placementActivitySummary == null        → same muted line (defensive).
 *   - placementActivitySummary.totalCount = 0 → muted "no proposed placements yet".
 *   - placementActivitySummary.totalCount = 1 → one-line summary + open icon.
 *   - placementActivitySummary.totalCount > 1 → one-line summary + [+N more] + open icon.
 */
export function PlacementActivitySummary({
  activeFtProject,
  placementActivitySummary,
  clusterName,
  onOpenTab,
}) {
  if (!activeFtProject || !placementActivitySummary) {
    return (
      <PlacementActivitySummaryRow>
        <span className="muted">No active FT engagement — placement activities n/a</span>
      </PlacementActivitySummaryRow>
    );
  }

  const { totalCount, latestActivity } = placementActivitySummary;

  if (totalCount === 0 || !latestActivity) {
    return (
      <PlacementActivitySummaryRow>
        <span className="muted">No proposed placements yet</span>
      </PlacementActivitySummaryRow>
    );
  }

  const targetProject = latestActivity.targetProjectName || '—';
  const targetClient = latestActivity.targetClientName || null;
  const sourceProject = activeFtProject.projectName || '—';
  const activityTypeName = latestActivity.activityType?.name || null;
  const activityTypeColor = latestActivity.activityType?.color || '#94A3B8';
  const statusName = latestActivity.statusType?.name || null;
  const statusColor = latestActivity.statusType?.color || '#94A3B8';
  const moreCount = totalCount - 1;

  return (
    <PlacementActivitySummaryRow>
      <span>
        <strong>
          {clusterName && <>{clusterName} • </>}
          {sourceProject}
        </strong>
        {' → '}
        <strong>{targetProject}</strong>
        {targetClient && <> · {targetClient}</>}
        {' | '}
        {formatDate(latestActivity.createdAt)}
        {' | '}
        {activityTypeName && (
          <span
            className="pa-pill"
            style={{
              backgroundColor: `${activityTypeColor}1A`,
              color: activityTypeColor,
            }}
          >
            {activityTypeName}
          </span>
        )}
        {statusName && (
          <>
            {' · '}
            <span style={{ color: statusColor, fontWeight: 500, fontSize: 11 }}>
              {statusName}
            </span>
          </>
        )}
      </span>
      {moreCount > 0 && <span className="pa-more">[+{moreCount} more]</span>}
      <Tooltip title="View all placement activities">
        <button
          type="button"
          className="pa-icon-button"
          onClick={onOpenTab}
          aria-label="View all placement activities"
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </button>
      </Tooltip>
    </PlacementActivitySummaryRow>
  );
}

export default PlacementActivitySummary;
